import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import PageTitle from "../../components/common/pages/PageTitle";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { useNotification } from "../../../utils/NotificationsContext";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid2,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import HistoryIcon from "@mui/icons-material/History";
import { useNavigate } from "react-router-dom";
import FormTitle from "../../components/common/form/FormTitle";
import FormSmallTextField from "../../components/common/form/FormTextField";
import AppDropDown from "../../components/common/filter/AppDropDown";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import { Customer } from "../../../models/Customer/Customer";
import ActivityLogDrawer from "../../components/common/drawer/ActivityLogDrawer";

interface AddCustomerProps {
  setLoadingForPopup?: (loading: boolean) => void;
  isNavigationNeeded?: boolean;
  onClose?: (customer: Customer) => void;
  customer_id?: number;
}

const AddCustomer: React.FC<AddCustomerProps> = ({
  setLoadingForPopup,
  isNavigationNeeded = true,
  onClose,
  customer_id,
}) => {
  const { customerStore, userStore } = useStores();
  const theme = useTheme();
  const location = useLocation();
  const { showError, showSuccess } = useNotification();
  let navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const customerId = queryParams.get("customer-id") || 0;
  const [isLoading, setIsLoading] = useState(false);
  const [isActivityDrawerOpen, setIsActivityDrawerOpen] = useState(false);
  const [isActivityLoading, setIsActivityLoading] = useState(false);

  const activeCoupon: FilterValue[] = [
    { id: "-1", value: "Select" },
    { id: "true", value: "True" },
    { id: "false", value: "False" },
  ];

  const activeRedemption: FilterValue[] = [
    { id: "-1", value: "Select" },
    { id: "true", value: "True" },
    { id: "false", value: "False" },
  ];
  const activeInventory: FilterValue[] = [
    { id: "-1", value: "Select" },
    { id: "true", value: "True" },
    { id: "false", value: "False" },
  ];
  const activeGOC: FilterValue[] = [
    { id: "-1", value: "Select" },
    { id: "true", value: "True" },
    { id: "false", value: "False" },
  ];
  const activeSez: FilterValue[] = [
    { id: "-1", value: "Select" },
    { id: "true", value: "True" },
    { id: "false", value: "False" },
  ];
  const activeCampaign: FilterValue[] = [
    { id: "-1", value: "Select" },
    { id: "true", value: "True" },
    { id: "false", value: "False" },
  ];
  const activeSwagstore: FilterValue[] = [
    { id: "-1", value: "Select" },
    { id: "true", value: "True" },
    { id: "false", value: "False" },
  ];
  const activeShopify: FilterValue[] = [
    { id: "-1", value: "Select" },
    { id: "true", value: "True" },
    { id: "false", value: "False" },
  ];

  const inventoryType: FilterValue[] = [
    { id: "-1", value: "Select" },
    { id: "1", value: "Master Inventory" },
    { id: "2", value: "Order Inventory" },
  ];

  useEffect(() => {
    // setIsLoading(true);
    if (customerId) {
      customerStore
        .getCustomerDetailsById(Number(customerId))
        .catch((err) => handleError(err?.response?.data?.message))
        .finally(() => setIsLoading(false));
    } else {
      customerStore.clearCustomerDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, customerStore]);

  const handleError = (message?: string) => {
    showError(
      "Error",
      message || "Something went wrong! Please try again later.",
    );
  };

  const validateForm = () => {
    const customerDetails = customerStore.customerDetails;

    if (!customerDetails?.name || customerDetails.name.trim() === "") {
      showError("Validation Error", "Name is required.");
      return false;
    }

    return true;
  };

  // const isValidEmail = (email: string) => {
  //   const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
  //   return regex.test(email);
  // };

  // const isValidUrl = (url: string) => {
  //   const regex = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
  //   return regex.test(url);
  // };

  const isViewOnly = (): boolean => {
    return (
      userStore.hasPermission(EmployeeRoles.VIEW_CUSTOMER) &&
      !userStore.hasPermission(EmployeeRoles.CREATE_CUSTOMER) &&
      !userStore.hasPermission(EmployeeRoles.EDIT_CUSTOMER)
    );
  };

  const isNoAccess = (): boolean => {
    return (
      !userStore.hasPermission(EmployeeRoles.VIEW_CUSTOMER) &&
      !userStore.hasPermission(EmployeeRoles.CREATE_CUSTOMER) &&
      !userStore.hasPermission(EmployeeRoles.EDIT_CUSTOMER)
    );
  };

  const saveDetails = () => {
    if (!validateForm()) return;
    if (customerId) {
      updateCustomer();
    } else {
      createCustomer();
    }
  };

  const createCustomer = () => {
    setIsLoading(true);
    customerStore
      .createCustomer({
        ...customerStore.customerDetails,
      })
      .then((res) => {
        if (setLoadingForPopup) {
          if (onClose) onClose(res.result);
          setIsLoading(false);
        } else {
          setIsLoading(false);
          showSuccess("Customer created successfully");
          navigate("/customers/listing");
        }
        customerStore.reset();
      })
      .catch((err) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => setIsLoading(false));
  };

  const updateCustomer = () => {
    setIsLoading(true);
    customerStore
      .updateCustomerDetails()
      .then((res) => {
        if (setLoadingForPopup) {
          setIsLoading(false);
          if (onClose) onClose(res);
        } else {
          setIsLoading(false);
          showSuccess("Customer updated successfully");
          navigate("/customers/listing");
        }
      })
      .catch((err) => {
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => setIsLoading(false));
  };

  const resetForm = () => {
    customerStore.discardChanges();
  };

  const handleOpenActivityDrawer = () => {
    if (!customerId) return;
    setIsActivityDrawerOpen(true);
    setIsActivityLoading(true);
    customerStore
      .getCustomerActivityLogs(Number(customerId))
      .catch((err) => console.log("error in getCustomerActivityLogs", err))
      .finally(() => setIsActivityLoading(false));
  };

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <PageTitle title={customerId ? "Update Customer" : "Create Customer"} />
        {customerId && (
          <Tooltip title="Activity History">
            <IconButton
              onClick={handleOpenActivityDrawer}
              sx={{
                mr: 8,
                backgroundColor: theme.palette.primary.main + "10",
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.main + "20",
                },
              }}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {isNoAccess() ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="10rem"
          textAlign="center"
        >
          <Typography variant="h6" fontWeight="500" color="text.secondary">
            You do not have permission to view this section.
          </Typography>
        </Box>
      ) : (
        // isLoading ? (
        //   <Box
        //     sx={{
        //       position: "fixed",
        //       top: 0,
        //       left: 0,
        //       right: 0,
        //       bottom: 0,
        //       display: "flex",
        //       justifyContent: "center",
        //       alignItems: "center",
        //     }}
        //   >
        //     <CircularProgress />
        //   </Box>
        // ) :
        <Grid2
          container
          spacing={{ xs: 2, sm: 4, md: 6, lg: 8 }}
          columns={{ xs: 12, sm: 12, md: 12, lg: 16 }}
        >
          <Grid2 size={{ xs: 12, sm: 12, md: 10, lg: 9 }}>
            <Paper
              elevation={1}
              sx={{ padding: { xs: 2, sm: 3, md: 4 }, mb: 4 }}
            >
              <FormTitle fontSize="12px" title="Name" />
              <FormSmallTextField
                label="Name"
                required
                variant="outlined"
                fontSize="12px"
                disabled={isViewOnly()}
                value={customerStore.customerDetails?.name || ""}
                onChange={(id, value) =>
                  customerStore.updateName(String(value))
                }
              />
              <FormTitle fontSize="12px" title="Legal Name" />
              <FormSmallTextField
                label="Legal Name"
                variant="outlined"
                fontSize="12px"
                disabled={isViewOnly()}
                value={customerStore.customerDetails?.legal_name || ""}
                onChange={(id, value) =>
                  customerStore.updateLegalName(String(value))
                }
              />
              <Box sx={{ display: "none" }}>
                <FormSmallTextField
                  label="Name"
                  required
                  variant="outlined"
                  fontSize="12px"
                  disabled={isViewOnly()}
                  value={customerStore.customerDetails?.id || ""}
                />
              </Box>
              <FormTitle fontSize="12px" title="Redemption URL" />
              <FormSmallTextField
                label="Add URL"
                variant="outlined"
                color="secondary"
                type="name"
                fontSize="12px"
                disabled={isViewOnly()}
                value={
                  customerStore.customerDetails?.config?.redemption_url || ""
                }
                onChange={(id, value) => customerStore.updateUrl(String(value))}
              />
              <FormTitle fontSize="12px" title="Redemption Email" />
              <FormSmallTextField
                label="Add Email"
                multiline
                variant="outlined"
                color="secondary"
                type="name"
                fontSize="12px"
                disabled={isViewOnly()}
                value={customerStore.customerDetails?.config?.email || ""}
                onChange={(id, value) =>
                  customerStore.updateRedemptionEmail(String(value))
                }
              />
              <Box sx={{ mb: 1 }}>
                <FormTitle title="Zoho Id" fontSize="12px" />
                <FormSmallTextField
                  backgroundColor="#cdcdcd4a"
                  label="Zoho Id"
                  variant="outlined"
                  color="secondary"
                  disabled={
                    !(
                      userStore.hasPermission(EmployeeRoles.ALL) ||
                      userStore.hasPermission(EmployeeRoles.EDIT_CUSTOMER)
                    )
                  }
                  type="name"
                  fontSize="12px"
                  value={customerStore.customerDetails?.zoho_id ?? ""}
                  onChange={(id, value) =>
                    customerStore.updateZohoId(String(value))
                  }
                />
              </Box>

              <FormTitle fontSize="12px" title="Redemption Email From" />
              <FormSmallTextField
                label="Add Name"
                variant="outlined"
                color="secondary"
                type="name"
                fontSize="12px"
                disabled={isViewOnly()}
                value={
                  customerStore.customerDetails?.config?.from_email_name || ""
                }
                onChange={(id, value) =>
                  customerStore.updateEmailFrom(String(value))
                }
              />
              {userStore.hasPermission(
                EmployeeRoles.MANAGE_SALES_ANALYTICS,
              ) && (
                <>
                  <Box sx={{ mb: 1 }}>
                    <FormTitle title="HR Order Id" fontSize="12px" />
                    <FormSmallTextField
                      label="HR Order Id"
                      variant="outlined"
                      color="secondary"
                      type="name"
                      fontSize="12px"
                      disabled={isViewOnly()}
                      value={customerStore.customerDetails?.hr_order_id ?? ""}
                      onChange={(id, value) =>
                        customerStore.updateHrOrderId(String(value))
                      }
                    />
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <FormTitle title="Marketing Order Id" fontSize="12px" />
                    <FormSmallTextField
                      label="Marketing Order Id"
                      variant="outlined"
                      color="secondary"
                      type="name"
                      fontSize="12px"
                      disabled={isViewOnly()}
                      value={
                        customerStore.customerDetails?.marketing_order_id ?? ""
                      }
                      onChange={(id, value) =>
                        customerStore.updateMarketingOrderId(String(value))
                      }
                    />
                  </Box>
                  <Box sx={{ mb: 1 }}>
                    <FormTitle title="Goldstandard Order Id" fontSize="12px" />
                    <FormSmallTextField
                      label="Goldstandard Order Id"
                      variant="outlined"
                      color="secondary"
                      type="name"
                      fontSize="12px"
                      disabled={isViewOnly()}
                      value={
                        customerStore.customerDetails?.goldstandard_order_id ??
                        ""
                      }
                      onChange={(id, value) =>
                        customerStore.updateGoldstandardOrderId(String(value))
                      }
                    />
                  </Box>
                </>
              )}
            </Paper>
            <Box
              sx={{
                display: isViewOnly() ? "none" : "flex",
                justifyContent: "space-around",
                mb: 10,
              }}
            >
              <Box sx={{ width: 1 / 3 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{ minHeight: "50px", padding: "6px 16px" }}
                  fullWidth
                  onClick={() => resetForm()}
                >
                  Reset
                </Button>
              </Box>
              <Box sx={{ width: 1 / 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => saveDetails()}
                  sx={{ minHeight: "50px", padding: "6px 16px" }}
                  fullWidth
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Save"
                  )}
                </Button>
              </Box>
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
            <Paper
              elevation={1}
              sx={{ padding: { xs: 2, sm: 3, md: 4 }, mb: 4 }}
            >
              <Box sx={{ mb: 3 }}>
                <FormTitle fontSize="12px" title="Coupon Redemption" />
              </Box>
              <AppDropDown
                title="Coupon Redemption"
                isMultiSelect={false}
                value={
                  customerStore.customerDetails?.config
                    ?.coupon_redemption_enabled !== undefined
                    ? Boolean(
                        customerStore.customerDetails?.config
                          ?.coupon_redemption_enabled,
                      )
                      ? "true"
                      : "false"
                    : "-1"
                }
                list={activeCoupon}
                height="36px"
                listHeight="30px"
                disabled={isViewOnly()}
                fontSize="12px"
                onSingleSelectionChange={(id) => {
                  customerStore.updateEnableCoupon(id === "true");
                }}
              />

              <Box sx={{ mb: 3, mt: 2 }}>
                <FormTitle fontSize="12px" title="Email Redemption" />
              </Box>
              <AppDropDown
                title="Email Redemption"
                isMultiSelect={false}
                value={
                  customerStore.customerDetails?.config
                    ?.send_redemption_email !== undefined
                    ? Boolean(
                        customerStore.customerDetails?.config
                          ?.send_redemption_email,
                      )
                      ? "true"
                      : "false"
                    : "-1"
                }
                list={activeRedemption}
                height="36px"
                listHeight="30px"
                fontSize="12px"
                disabled={isViewOnly()}
                onSingleSelectionChange={(id) => {
                  customerStore.updateEnableRedemptionEmail(id === "true");
                }}
              />
              <Box sx={{ mb: 3, mt: 2 }}>
                <FormTitle fontSize="12px" title="Inventory" />
              </Box>
              <AppDropDown
                title="Inventory"
                isMultiSelect={false}
                disabled={isViewOnly()}
                value={
                  customerStore.customerDetails?.config?.inventory_enabled !==
                  undefined
                    ? Boolean(
                        customerStore.customerDetails?.config
                          ?.inventory_enabled,
                      )
                      ? "true"
                      : "false"
                    : "-1"
                }
                list={activeInventory}
                height="36px"
                listHeight="30px"
                fontSize="12px"
                onSingleSelectionChange={(id) => {
                  customerStore.updateEnableInventory(id === "true");
                }}
              />
              <Box sx={{ mb: 3, mt: 2 }}>
                <FormTitle fontSize="12px" title="GOC" />
              </Box>
              <AppDropDown
                title="GOC"
                isMultiSelect={false}
                disabled={isViewOnly()}
                value={
                  customerStore.customerDetails?.config
                    ?.gift_of_choice_enabled !== undefined
                    ? Boolean(
                        customerStore.customerDetails?.config
                          ?.gift_of_choice_enabled,
                      )
                      ? "true"
                      : "false"
                    : "-1"
                }
                list={activeGOC}
                height="36px"
                listHeight="30px"
                fontSize="12px"
                onSingleSelectionChange={(id) => {
                  customerStore.updateEnableGOC(id === "true");
                }}
              />
              <Box sx={{ mb: 3, mt: 2 }}>
                <FormTitle fontSize="12px" title="Sez" />
              </Box>
              <AppDropDown
                title="Sez"
                isMultiSelect={false}
                disabled={isViewOnly()}
                value={
                  customerStore.customerDetails?.config?.is_sez !== undefined
                    ? Boolean(customerStore.customerDetails?.config?.is_sez)
                      ? "true"
                      : "false"
                    : "-1"
                }
                list={activeSez}
                height="36px"
                listHeight="30px"
                fontSize="12px"
                onSingleSelectionChange={(id) => {
                  customerStore.updateEnableSez(id === "true");
                }}
              />
              <Box sx={{ mb: 3, mt: 2 }}>
                <FormTitle fontSize="12px" title="Campaign" />
              </Box>
              <AppDropDown
                title="Campaign"
                isMultiSelect={false}
                disabled={isViewOnly()}
                value={
                  customerStore.customerDetails?.config?.campaigns_enabled !==
                  undefined
                    ? Boolean(
                        customerStore.customerDetails?.config
                          ?.campaigns_enabled,
                      )
                      ? "true"
                      : "false"
                    : "-1"
                }
                list={activeCampaign}
                height="36px"
                listHeight="30px"
                fontSize="12px"
                onSingleSelectionChange={(id) => {
                  customerStore.updateEnableCampaign(id === "true");
                }}
              />
              <Box sx={{ mb: 3, mt: 2 }}>
                <FormTitle fontSize="12px" title="Swagstore" />
              </Box>
              <AppDropDown
                title="Swagstore"
                isMultiSelect={false}
                disabled={isViewOnly()}
                value={
                  customerStore.customerDetails?.config?.swagstore_enabled !==
                  undefined
                    ? Boolean(
                        customerStore.customerDetails?.config
                          ?.swagstore_enabled,
                      )
                      ? "true"
                      : "false"
                    : "-1"
                }
                list={activeSwagstore}
                height="36px"
                listHeight="30px"
                fontSize="12px"
                onSingleSelectionChange={(id) => {
                  customerStore.updateEnableSwagStore(id === "true");
                }}
              />
              <Box sx={{ mb: 3, mt: 2 }}>
                <FormTitle fontSize="12px" title="Shopify" />
              </Box>
              <AppDropDown
                title="Shopify"
                isMultiSelect={false}
                disabled={isViewOnly()}
                value={
                  customerStore.customerDetails?.config?.shopify_enabled !==
                  undefined
                    ? Boolean(
                        customerStore.customerDetails?.config?.shopify_enabled,
                      )
                      ? "true"
                      : "false"
                    : "-1"
                }
                list={activeShopify}
                height="36px"
                listHeight="30px"
                fontSize="12px"
                onSingleSelectionChange={(id) => {
                  customerStore.updateEnableShopify(id === "true");
                }}
              />
              <Box sx={{ mb: 3, mt: 2 }}>
                <FormTitle fontSize="12px" title="Inventory Type" />
              </Box>
              <AppDropDown
                title="Select Type"
                isMultiSelect={false}
                value={
                  customerStore.customerDetails?.config?.inventory_type !==
                    undefined &&
                  customerStore.customerDetails!.config!.inventory_type !== null
                    ? customerStore.customerDetails!.config!.inventory_type.toString()
                    : "-1"
                }
                list={inventoryType}
                height="36px"
                listHeight="30px"
                disabled={isViewOnly()}
                fontSize="12px"
                onSingleSelectionChange={(id) => {
                  customerStore.updateInventoryType(id);
                }}
              />
            </Paper>
          </Grid2>
        </Grid2>
      )}
      <ActivityLogDrawer
        isOpen={isActivityDrawerOpen}
        onClose={() => setIsActivityDrawerOpen(false)}
        activityLogs={customerStore.activityLogs}
        isLoading={isActivityLoading}
        title="Activity History"
        subtitle={customerId ? `Customer #${customerId}` : null}
      />
    </Container>
  );
};

export default observer(AddCustomer);
